/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { mapBy, setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import config from 'linced-admin/config/environment';
import { mapBy as mapByArray } from '@linc-technologies/linced-common/utils/arrays';

export default class SearchController extends Controller {
  @service
  fetch;

  @service
  store;

  @service
  toaster;

  PersonType = PersonType;

  @tracked
  emailAddress = '';

  @action
  setEmailAddress(value) {
    this.emailAddress = value;
  }

  @tracked
  searching = false;

  @tracked
  hasSearched = false;

  @tracked
  result = null;

  @tracked
  resultEmail = '';

  @computed(
    'result.{type,caregiver.schools.[]}',
    'model.schools.@each.{id,name}',
  )
  get schools() {
    if (isEmpty(this.result)) {
      return [];
    }
    let schoolIds = [];
    switch (this.result.type) {
      case PersonType.LEARNER:
        schoolIds = mapByArray(
          this.result.get('learner.enrolments') ?? [],
          'schoolId',
        );
        break;

      case PersonType.CAREGIVER:
        schoolIds = this.result.get('caregiver.schools') ?? [];
        break;

      case PersonType.STAFF:
        if (!isEmpty(this.result.get('staff.schoolId'))) {
          schoolIds.push(this.result.get('staff.schoolId'));
        }
    }
    if (isEmpty(schoolIds)) {
      return [];
    }

    return this.model.schools.filter((school) => schoolIds.includes(school.id));
  }

  @mapBy('schools', 'name')
  schoolNames;

  @tracked
  dialogOpen = false;

  @setDiff('model.schools', 'schools')
  availableSchools;

  @tracked
  selectedSchool = null;

  @action
  setSelectedSchool(value) {
    this.selectedSchool = value;
  }

  @tracked
  saveInProgress = false;

  @tracked
  studentNames = null;

  @action
  search() {
    this.searching = true;
    this.hasSearched = true;
    this.result = null;
    this.selectedSchool = null;
    this.studentNames = [];

    // Search users first, as they're indexed by username/email address.
    this.store
      .query('user', { username: this.emailAddress })
      .then((results) => {
        if (isEmpty(results) || isEmpty(results[0])) {
          // No matching users; search for a userless caregiver.
          let query = encodeURIComponent(this.emailAddress);
          let url = `${config.APP.apiHost}/people/v4/caregivers/:search?query=${query}`;
          return this.fetch.request(url).then((results) => {
            if (
              !!results &&
              !!results.caregivers &&
              results.caregivers.length
            ) {
              let result = results.caregivers[0];
              return this.store.findRecord('person', result.caregiverId);
            }
          });
        }

        return this.store.findRecord('person', results[0].personId);
      })
      .then((person) => {
        this.resultEmail = this.emailAddress;
        this.result = person;

        if (!isEmpty(person) && person.type === PersonType.CAREGIVER) {
          let learnerIds = person.get('caregiver.learnerIds') ?? [];
          if (learnerIds.length >= 1) {
            let people = JSON.stringify([...learnerIds]);

            // Deliberately not returning this promise.
            // Student names will appear when it resolves.
            this.store.query('person', { people }).then((results) => {
              this.studentNames = results.map((person) => person.fullName);
            });
          }
        }
      })
      .finally(() => {
        this.searching = false;
      });
  }

  @action
  resultField(key) {
    return this.get(`result.fields.${key}.value`);
  }

  @action
  toggleDialog() {
    this.toggleProperty('dialogOpen');
    this.selectedSchool = null;
  }

  @action
  addToSchool() {
    if (
      isEmpty(this.selectedSchool) ||
      isEmpty(this.result) ||
      this.result.type !== PersonType.CAREGIVER
    ) {
      // Not a valid state to be adding a caregiver to a school.  Abort.
      return;
    }

    this.saveInProgress = true;

    let url = `${config.APP.apiHost}/people/v4/caregivers/${this.result.id}/:enableTenantAccess`;
    let data = {
      personId: this.result.id,
      schoolId: this.selectedSchool.id,
    };
    let options = {
      data: JSON.stringify(data),
    };

    this.fetch
      .post(url, options)
      .then(() => {
        return this.result.reload();
      })
      .then(() => {
        this.toaster.success('Connected successfully!');
        this.dialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Failed to connect!');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }
}
